import { inject } from '@angular/core';
import {
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpRequest,
  HttpHandlerFn,
} from '@angular/common/http';
import { catchError, throwError, switchMap } from 'rxjs';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Router } from '@angular/router';
import { LoggerService } from '../services';
import { APP_CONSTANTS } from '../constants';
import { AuthStore } from '@features/auth/auth.store';
import { AuthService } from '@features/auth/services/auth.service';
import { AppPaths } from '@core/routes/app-paths';
import { isTokenExpired, isTokenExpiringSoon } from '@core/utils/jwt.utils';
import {
  CLAIM_SLOT_CONFLICT_USER_MESSAGE,
  isForbiddenBusinessErrorWithoutLoginRedirect,
  resolveClaimApiErrorMessage,
} from '@core/constants/job-claim-api-error-messages';
import {
  resolveApiErrorMessageByCode,
  shouldSuppressGlobalToastForApiError,
} from '@core/constants/api-error-codes';
import { environment } from '@environments/environment';

/** Shown when session is invalid/expired and user is redirected to login. */
const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';

/**
 * Presigned uploads (S3, etc.) must not receive API headers: Bearer auth breaks signature,
 * Accept: application/json is wrong for binary PUT, and Content-Type must match the signed value only.
 */
function shouldBypassInterceptorForExternalBlobUpload(req: HttpRequest<unknown>): boolean {
  const body = req.body as unknown;
  const hasBlobLikeBody =
    (typeof Blob !== 'undefined' && body instanceof Blob) ||
    (typeof FormData !== 'undefined' && body instanceof FormData);
  if (!hasBlobLikeBody) {
    return false;
  }
  if (!req.url.startsWith('http://') && !req.url.startsWith('https://')) {
    return false;
  }
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : undefined;
    const targetHost = new URL(req.url, origin).hostname.toLowerCase();
    // Resolve against the current origin so a relative apiUrl (LAN proxy setup)
    // still parses; for an absolute apiUrl the base is ignored (no behavior change).
    const apiHost = new URL(environment.apiUrl, origin).hostname.toLowerCase();
    return targetHost !== apiHost;
  } catch {
    return false;
  }
}

/**
 * JSON Content-Type must not be forced on Blob/File PUT (e.g. S3 presigned upload) or FormData;
 * the client sets the correct Content-Type for those bodies.
 */
function buildDefaultHeaders(req: HttpRequest<unknown>): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  const body = req.body as unknown;
  const skipJsonContentType =
    (typeof Blob !== 'undefined' && body instanceof Blob) ||
    (typeof FormData !== 'undefined' && body instanceof FormData);
  if (!skipJsonContentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
}

/**
 * Parallel HTTP requests share one refresh via getOrRunRefresh() + shareReplay; on failure
 * every subscriber's catchError runs, which would show duplicate toasts and repeat logout/navigate.
 */
const SESSION_EXPIRY_SIDE_EFFECTS_DEBOUNCE_MS = 4000;
let sessionExpirySideEffectsLastAt = 0;

function runSessionExpiredSideEffectsOnce(
  logger: LoggerService,
  message: NzMessageService,
  router: Router,
  authStore: AuthStore,
  logLabel: string,
  err: unknown,
): void {
  const now = Date.now();
  if (now - sessionExpirySideEffectsLastAt < SESSION_EXPIRY_SIDE_EFFECTS_DEBOUNCE_MS) {
    return;
  }
  sessionExpirySideEffectsLastAt = now;
  logger.error(logLabel, err);
  message.error(SESSION_EXPIRED_MESSAGE, { nzDuration: 5000 });
  localStorage.removeItem(APP_CONSTANTS.STORAGE.USER);
  authStore.logout();
  if (!router.url.includes(`/${AppPaths.AUTH.ROOT}/${AppPaths.AUTH.LOGIN}`)) {
    router.navigate([`/${AppPaths.AUTH.ROOT}/${AppPaths.AUTH.LOGIN}`]);
  }
}

/**
 * Universal HTTP interceptor that handles:
 * - Authentication token injection
 * - Automatic token refresh
 * - Common headers
 * - Error handling and redirects
 */
export const httpInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const message = inject(NzMessageService);
  const router = inject(Router);
  const logger = inject(LoggerService);
  const authStore = inject(AuthStore);
  const authService = inject(AuthService);

  if (shouldBypassInterceptorForExternalBlobUpload(req)) {
    return next(req);
  }

  // Get token from store or session blob in localStorage
  const token =
    authStore.token() ||
    (() => {
      const session = authService.getStoredSession();
      return session?.accessToken ?? null;
    })();

  const headers = buildDefaultHeaders(req);

  const isAuthRequest =
    req.url.includes('/auth/login') ||
    req.url.includes('/auth/register') ||
    req.url.includes('/auth/register/send-otp') ||
    req.url.includes('/auth/register/verify-otp') ||
    req.url.includes('/auth/refresh') ||
    req.url.includes('/auth/forgot-password');
  const isLogoutRequest = req.url.includes('/auth/logout');
  const isRefreshRequest = req.url.includes('/auth/refresh');
  const isGoogleApiRequest =
    req.url.includes('places.googleapis.com') || req.url.includes('maps.googleapis.com');

  // Add authorization header for authenticated requests (including logout which needs token to invalidate session)
  if (!isAuthRequest && !isGoogleApiRequest && token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Proactive refresh: if access token is expired or expiring soon (1 min), refresh before sending
  const needsAuth = !isAuthRequest && !isGoogleApiRequest && token;
  const shouldRefreshProactively =
    needsAuth && (isTokenExpired(token) || isTokenExpiringSoon(token, 1));

  if (shouldRefreshProactively) {
    logger.log('Access token expired or expiring soon, refreshing proactively...');
    return authService.getOrRunRefresh().pipe(
      switchMap(tokens => {
        authService.updateTokens(tokens);
        authStore.loadFromStorage();
        const retryReq = req.clone({
          setHeaders: {
            ...headers,
            Authorization: `Bearer ${tokens.accessToken}`,
          },
        });
        return next(retryReq);
      }),
      catchError(refreshError => {
        runSessionExpiredSideEffectsOnce(
          logger,
          message,
          router,
          authStore,
          'Proactive token refresh failed:',
          refreshError,
        );
        return throwError(() => refreshError);
      }),
    );
  }

  // Clone request with headers
  const modifiedReq = req.clone({
    setHeaders: headers,
  });

  return next(modifiedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage: string | undefined;

      if (error.error instanceof ErrorEvent) {
        // Client-side error
        errorMessage = `Error: ${error.error.message}`;
        logger.error('Client-side error:', error.error);
      } else {
        // Server-side error
        // Handle cases where server returns HTML instead of JSON (e.g., 404 page)
        if (typeof error.error === 'string' && error.error.trim().startsWith('<')) {
          // HTML response (likely 404 or error page)
          logger.error(
            `Backend returned HTML instead of JSON (${error.status}) on ${modifiedReq.url}`,
          );
          if (error.status === 404) {
            errorMessage = 'Endpoint not found. Please check the API configuration.';
          } else {
            errorMessage = `Server error (${error.status}). Please try again later.`;
          }
        } else if (error.error && typeof error.error === 'object') {
          // JSON error response - support multiple formats (NestJS, Express, etc.)
          const msg = error.error.message;
          if (Array.isArray(msg) && msg.length > 0) {
            // NestJS ValidationPipe: message is string[]
            // Or: message is { message: string }[]
            errorMessage = msg
              .map((m: unknown) =>
                typeof m === 'string' ? m : (m as { message?: string })?.message,
              )
              .filter(Boolean)
              .join('; ');
          } else if (typeof msg === 'string') {
            errorMessage = msg;
          } else {
            errorMessage = error.error?.error ?? undefined; // e.g. "Bad Request"
          }
          if (!errorMessage && error.status === 400) {
            errorMessage = 'Invalid request. Please check your input.';
          }
          logger.error(`Backend error ${error.status} on ${modifiedReq.url}:`, error.error);
        } else {
          // Unknown error format (error.error is null, or not object/string)
          logger.error(`Backend error ${error.status} on ${modifiedReq.url}:`, error.error);
          if (error.status === 404) {
            errorMessage = 'Endpoint not found. Please check the API configuration.';
          } else if (error.status >= 500) {
            errorMessage = 'Server error. Please try again later.';
          } else if (error.status === 400) {
            errorMessage = 'Invalid request. Please check your input.';
          } else {
            errorMessage = `Request failed with status ${error.status}.`;
          }
        }

        if (typeof error.error === 'object' && error.error !== null) {
          const mappedCommonCodeMessage = resolveApiErrorMessageByCode(error.error);
          if (mappedCommonCodeMessage) {
            errorMessage = mappedCommonCodeMessage;
          }

          const claimMsg = resolveClaimApiErrorMessage(error.error);
          if (claimMsg) {
            errorMessage = claimMsg;
          }
        }
        if (error.status === 409 && !errorMessage?.trim()) {
          errorMessage = CLAIM_SLOT_CONFLICT_USER_MESSAGE;
        }

        // Handle auth errors only for non-auth requests (but allow logout to handle its own errors)
        if (!isAuthRequest && !isGoogleApiRequest && !isLogoutRequest) {
          switch (error.status) {
            case 401: {
              // Prevent infinite retry: if this request was already retried, don't try again
              const alreadyRetried = (req as HttpRequest<unknown> & { _retried?: boolean })
                ._retried;
              if (alreadyRetried) {
                runSessionExpiredSideEffectsOnce(
                  logger,
                  message,
                  router,
                  authStore,
                  '401 after retry — forcing logout',
                  error,
                );
                return throwError(() => error);
              }

              logger.log('Access token expired, attempting refresh...');
              return authService.getOrRunRefresh().pipe(
                switchMap(tokens => {
                  logger.log('Token refresh successful');
                  authService.updateTokens(tokens);
                  authStore.loadFromStorage();
                  const retryReq = req.clone({
                    setHeaders: {
                      ...headers,
                      Authorization: `Bearer ${tokens.accessToken}`,
                    },
                  });
                  // Tag the retried request to avoid infinite loops
                  (retryReq as HttpRequest<unknown> & { _retried?: boolean })._retried = true;
                  return next(retryReq);
                }),
                catchError(refreshError => {
                  runSessionExpiredSideEffectsOnce(
                    logger,
                    message,
                    router,
                    authStore,
                    'Token refresh failed:',
                    refreshError,
                  );
                  return throwError(() => refreshError);
                }),
              );
            }
            case 403: {
              errorMessage =
                errorMessage?.trim() ||
                'Access denied. You do not have permission to access this resource.';

              if (
                !isForbiddenBusinessErrorWithoutLoginRedirect(error.error) &&
                !router.url.includes(`/${AppPaths.AUTH.ROOT}/${AppPaths.AUTH.LOGIN}`)
              ) {
                router.navigate([`/${AppPaths.AUTH.ROOT}/${AppPaths.AUTH.LOGIN}`]);
              }
              break;
            }
          }
        }
      }

      // Refresh failures are handled by session-expired UX (toast + logout). Avoid a second toast
      // with raw API text (e.g. "Refresh token is missing", REFRESH_TOKEN_MISSING).
      const suppressToastForHandledAuthRefresh =
        isRefreshRequest && (error.status === 401 || error.status === 400);
      const suppressToastForApiCode = shouldSuppressGlobalToastForApiError(
        error.error,
        modifiedReq.url,
      );

      if (errorMessage && !suppressToastForHandledAuthRefresh && !suppressToastForApiCode) {
        message.error(errorMessage, { nzDuration: 8000 });
      }

      return throwError(() => error);
    }),
  );
};
